import { z } from 'zod'
import { optionalString } from '../../primitives'

/**
 * Constitución y registro de la persona jurídica — espejo enfocado del FEIC interno.
 * DOS caras según el tipo de entidad:
 * - `commercialSociety` (entidad lucrativa): escritura pública + patentes de sociedad y
 *   de comercio, con modificaciones a la escritura.
 * - `otherSociety` (no lucrativa / organismo internacional): documento de creación
 *   (acuerdo gubernativo o similar) + datos de registro, con modificaciones.
 *
 * Todos los campos son opcionales a nivel schema (auto-save parcial); la exigencia
 * condicional (requerido salvo jurisdicción extranjera) la aplica la capa web. El
 * `typeModification` se captura como string (catálogo en la capa web).
 */

/** Escritura pública (o su modificación). */
export const PublicDeedSchema = z.object({
  id: optionalString(),
  number: optionalString(),
  date: optionalString(),
  notary: optionalString(),
  typeModification: optionalString()
})

/** Patente (de sociedad o de comercio): número + folio + libro. */
export const PatentSchema = z.object({
  number: optionalString(),
  sheet: optionalString(),
  book: optionalString()
})

/** Sociedad mercantil (entidad lucrativa). */
export const CommercialSocietySchema = z.object({
  publicDeed: PublicDeedSchema.default({}),
  modificationPublicDeed: z.array(PublicDeedSchema).default([]),
  companyPatent: PatentSchema.default({}),
  commercialPatent: PatentSchema.default({})
})

/** Documento de creación (acuerdo gubernativo o similar) o su modificación. */
export const CreationDocumentSchema = z.object({
  id: optionalString(),
  number: optionalString(),
  date: optionalString(),
  authorizer: optionalString(),
  typeModification: optionalString()
})

/** Datos de registro de "otro tipo de sociedad". */
export const RegistrationDataSchema = z.object({
  number: optionalString(),
  sheet: optionalString(),
  book: optionalString(),
  institution: optionalString()
})

/** Otro tipo de sociedad (no lucrativa / organismo internacional). */
export const OtherSocietySchema = z.object({
  creationDocument: CreationDocumentSchema.default({}),
  modificationCreationDocument: z.array(CreationDocumentSchema).default([]),
  registrationData: RegistrationDataSchema.default({})
})

export type PublicDeed = z.infer<typeof PublicDeedSchema>
export type Patent = z.infer<typeof PatentSchema>
export type CommercialSociety = z.infer<typeof CommercialSocietySchema>
export type CreationDocument = z.infer<typeof CreationDocumentSchema>
export type RegistrationData = z.infer<typeof RegistrationDataSchema>
export type OtherSociety = z.infer<typeof OtherSocietySchema>
